import axios from 'axios';

/**
 * Admin Exchange Rate Service
 * Handles Pi to NGN exchange rate management for administrators
 * Uses the global axios interceptors from AdminAuthService for authentication
 */
class AdminExchangeRateService {
  /**
   * Get current Pi to NGN exchange rate
   */
  async getCurrentRate() {
    try {
      const response = await axios.get('/api/admin/management/exchange-rate');
      return response.data.data;
    } catch (error) {
      console.error('Get current exchange rate error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update Pi to NGN exchange rate
   * @param {number} rate - New exchange rate (1 Pi = ₦rate)
   */
  async updateRate(rate) {
    try {
      const response = await axios.put('/api/admin/management/exchange-rate', { rate });
      return response.data.data;
    } catch (error) {
      console.error('Update exchange rate error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get exchange rate history
   */
  async getRateHistory() {
    try {
      const response = await axios.get('/api/admin/management/exchange-rate/history');
      return response.data.data;
    } catch (error) {
      console.error('Get rate history error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Check if exchange rate is set
   */
  async isRateSet() {
    try {
      const response = await axios.get('/api/admin/management/exchange-rate/status');
      return response.data.data;
    } catch (error) {
      console.error('Check rate status error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data } = error.response;
      return {
        message: data.error?.message || 'An error occurred',
        code: data.error?.code || 'API_ERROR',
        status: error.response.status,
        response: error.response,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0,
      };
    }
  }

  /**
   * Format exchange rate for display
   */
  formatRateForDisplay(rateData) {
    if (!rateData || !rateData.rate) return null;
    
    const rateValue = parseFloat(rateData.rate);
    
    return {
      rate: rateValue,
      formattedRate: `1 Pi = ₦${rateValue.toLocaleString()}`,
      formattedDescription: rateData.description || `1 Pi = ₦${rateValue.toLocaleString()}`,
      updatedAt: rateData.updatedAt ? new Date(rateData.updatedAt).toLocaleString() : 'N/A',
      updatedBy: rateData.updatedBy || 'Unknown',
      isSet: !!rateValue
    };
  }

  /**
   * Format rate history for display
   */
  formatRateHistory(history) {
    if (!history || !Array.isArray(history)) return [];
    
    return history.map(record => ({
      id: record.id,
      rate: record.rate,
      formattedRate: `1 Pi = ₦${parseFloat(record.rate).toLocaleString()}`,
      fromCurrency: record.from_currency,
      toCurrency: record.to_currency,
      source: record.source,
      createdAt: new Date(record.created_at).toLocaleString(),
      updatedBy: record.updated_by || 'Unknown',
      description: `Updated by ${record.updated_by || 'Unknown'} at ${new Date(record.created_at).toLocaleString()}`
    })).reverse(); // Most recent first
  }

  /**
   * Validate exchange rate input
   */
  validateRateInput(rate) {
    if (!rate && rate !== 0) {
      throw new Error('Exchange rate is required');
    }
    
    const numRate = parseFloat(rate);
    if (isNaN(numRate)) {
      throw new Error('Exchange rate must be a valid number');
    }
    
    if (numRate <= 0) {
      throw new Error('Exchange rate must be greater than 0');
    }
    
    if (numRate > 1000000) {
      throw new Error('Exchange rate cannot exceed 1,000,000');
    }
    
    if (numRate < 0.000001) {
      throw new Error('Exchange rate cannot be less than 0.000001');
    }
    
    // Check if it's a valid decimal number (not "nearest" nonsense)
    if (rate.toString().match(/[^\d.]/)) {
      throw new Error('Exchange rate can only contain numbers and decimal point');
    }
    
    // Check for multiple decimal points
    if ((rate.match(/\./g) || []).length > 1) {
      throw new Error('Exchange rate can only have one decimal point');
    }
    
    // Check for trailing decimal point
    if (rate.endsWith('.')) {
      throw new Error('Exchange rate cannot end with a decimal point');
    }
    
    return numRate;
  }

  /**
   * Get rate change percentage from previous rate
   */
  getRateChangePercentage(currentRate, previousRate) {
    if (!previousRate) return null;
    
    const change = currentRate - previousRate;
    const percentage = (change / previousRate) * 100;
    
    return {
      absolute: change,
      percentage: percentage,
      formattedPercentage: `${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%`,
      direction: percentage > 0 ? 'increase' : percentage < 0 ? 'decrease' : 'no_change'
    };
  }
}

// Export singleton instance
export default new AdminExchangeRateService();